import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, Check } from 'lucide-react';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success('¡Cuenta creada!');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar');
    } finally {
      setLoading(false);
    }
  };

  const benefits = ['14 días de prueba gratis', 'Sin tarjeta de crédito', 'Cancela cuando quieras'];

  return (
    <div className="min-h-screen bg-[#0c0c0d] flex">
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <Link to="/" className="flex items-center gap-2.5 mb-14">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <span className="text-white font-bold">N</span>
            </div>
            <span className="text-xl font-semibold text-white">Nova</span>
          </Link>

          <h1 className="text-headline text-white mb-2">Crear cuenta</h1>
          <p className="text-[#6e6e73] mb-10">Comienza tu prueba gratuita de 14 días</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-caption block mb-2">Nombre</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name" className="nova-input" placeholder="Tu nombre" />
            </div>
            <div>
              <label className="text-caption block mb-2">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email" className="nova-input" placeholder="tu@email.com" />
            </div>
            <div>
              <label className="text-caption block mb-2">Contraseña</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="register-password" className="nova-input pr-12" placeholder="Mínimo 6 caracteres" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6e6e73] hover:text-white transition-colors">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="register-submit" className="w-full nova-btn nova-btn-primary py-3">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creando...</> : 'Crear cuenta'}
            </button>
          </form>

          <div className="mt-8 space-y-2">
            {benefits.map((b) => (
              <div key={b} className="flex items-center gap-2 text-sm text-[#6e6e73]">
                <Check className="w-4 h-4 text-indigo-400" />
                <span>{b}</span>
              </div>
            ))}
          </div>

          <p className="mt-10 text-center text-[#6e6e73]">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-indigo-400 hover:text-indigo-300 font-medium" data-testid="login-link">Inicia sesión</Link>
          </p>
        </motion.div>
      </div>

      <div className="hidden lg:flex flex-1 items-center justify-center bg-[#111113] border-l border-white/[0.04] relative overflow-hidden">
        <div className="absolute inset-0 nova-grid-pattern opacity-30" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[100px]" />
        <div className="relative text-center px-12">
          <h2 className="text-4xl font-bold text-white mb-4">Tu negocio,<br />en tus manos</h2>
          <p className="text-[#6e6e73] text-lg">Únete a miles de negocios que<br />ya confían en Nova</p>
        </div>
      </div>
    </div>
  );
}
